import { useState } from "react";
import {
  Calculator,
  ArrowRight,
  Globe,
  Info,
  Loader2,
  AlertTriangle,
  Check,
  Ship,
} from "lucide-react";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CORRIDOR_GROUPS = [
  {
    label: "\u{1F1E8}\u{1F1F3} Depuis la Chine",
    corridors: [
      { id: "cn_eu", to: "\u{1F1EA}\u{1F1FA} Union Européenne" },
      { id: "cn_uk", to: "\u{1F1EC}\u{1F1E7} Royaume-Uni" },
      { id: "cn_us", to: "\u{1F1FA}\u{1F1F8} États-Unis" },
    ],
  },
  {
    label: "\u{1F1EA}\u{1F1FA} Depuis l'Union Européenne",
    corridors: [
      { id: "eu_uk", to: "\u{1F1EC}\u{1F1E7} Royaume-Uni" },
      { id: "eu_us", to: "\u{1F1FA}\u{1F1F8} États-Unis" },
      { id: "eu_eu", to: "\u{1F1EA}\u{1F1FA} Union Européenne", tag: "0€ FRAIS" },
    ],
  },
  {
    label: "\u{1F1EC}\u{1F1E7} Depuis le Royaume-Uni",
    corridors: [
      { id: "uk_eu", to: "\u{1F1EA}\u{1F1FA} Union Européenne" },
      { id: "uk_us", to: "\u{1F1FA}\u{1F1F8} États-Unis" },
    ],
  },
  {
    label: "\u{1F1FA}\u{1F1F8} Depuis les États-Unis",
    corridors: [
      { id: "us_eu", to: "\u{1F1EA}\u{1F1FA} Union Européenne" },
      { id: "us_cn", to: "\u{1F1E8}\u{1F1F3} Chine" },
    ],
  },
];

export default function CustomsCalculator() {
  const [corridor, setCorridor] = useState("");
  const [productValue, setProductValue] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleCalculate = async (e) => {
    e.preventDefault();
    if (!corridor || !productValue) {
      toast.error("Sélectionnez un corridor et entrez la valeur du produit");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/customs/calculate`, {
        corridor,
        product_value: parseFloat(productValue),
        shipping_cost: parseFloat(shippingCost) || 0,
      });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de calcul");
    } finally {
      setLoading(false);
    }
  };

  const isIntraEU = corridor === "eu_eu";

  return (
    <div className="space-y-6" data-testid="customs-calculator-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <Calculator className="text-[#00E676]" size={36} />
          CALCULATEUR DOUANE
        </h1>
        <p className="text-muted-foreground mt-1 uppercase tracking-wider text-xs sm:text-sm">
          Frais de douane et d'importation — Réglementation 2026
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleCalculate} className="neo-card p-4 sm:p-6 space-y-5">
        {/* Corridor Selection */}
        <div>
          <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-3 block">
            Corridor d'importation
          </label>
          <div className="space-y-4">
            {CORRIDOR_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-xs font-bold uppercase tracking-wider mb-2">{group.label}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {group.corridors.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => { setCorridor(c.id); setResult(null); }}
                      className={`p-3 border-2 text-left flex items-center gap-2 transition-all text-sm ${
                        corridor === c.id
                          ? "bg-[#00E676]/10 border-[#00E676] shadow-[3px_3px_0px_#0A0A0A]"
                          : "border-[#0A0A0A] dark:border-[#00E676]/30 hover:bg-[#00E676]/5"
                      } ${c.tag ? "bg-[#00E676]/5" : ""}`}
                      data-testid={`corridor-${c.id}`}
                    >
                      <ArrowRight size={14} className="text-[#00E676] flex-shrink-0" />
                      <span className="flex-1">{c.to}</span>
                      {c.tag && (
                        <span className="text-[10px] bg-[#00E676] text-[#0A0A0A] px-2 py-0.5 font-bold flex-shrink-0">{c.tag}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Value inputs */}
        {corridor && !isIntraEU && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1 block">
                Valeur du produit (€)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Ex: 150.00"
                value={productValue}
                onChange={(e) => setProductValue(e.target.value)}
                className="neo-input"
                data-testid="product-value-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1 block">
                Frais de livraison (€)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Ex: 15.00"
                value={shippingCost}
                onChange={(e) => setShippingCost(e.target.value)}
                className="neo-input"
                data-testid="shipping-cost-input"
              />
            </div>
          </div>
        )}

        {/* Intra-EU message */}
        {isIntraEU && (
          <div className="bg-[#00E676]/10 border-2 border-[#00E676] p-4 sm:p-6" data-testid="intra-eu-message">
            <div className="flex items-start gap-3">
              <Check className="text-[#00E676] flex-shrink-0 mt-0.5" size={24} />
              <div>
                <h3 className="font-extrabold text-lg mb-1">AUCUN frais de douane</h3>
                <p className="text-sm text-muted-foreground">
                  Le marché unique européen garantit la <strong>libre circulation des marchandises</strong> entre tous les pays membres de l'Union Européenne.
                  Aucun droit de douane, aucune TVA supplémentaire, aucun frais de dédouanement.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  La TVA est déjà incluse dans le prix d'achat au sein de l'UE.
                </p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {["FR","DE","IT","ES","NL","BE","AT","PT","IE","FI","SE","DK","PL","CZ","RO","HU","BG","HR","SK","SI","LT","LV","EE","LU","MT","CY"].map(c => (
                    <span key={c} className="text-[10px] bg-[#0A0A0A] text-[#00E676] px-1.5 py-0.5 font-bold">{c}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        {!isIntraEU && corridor && (
          <button
            type="submit"
            disabled={loading || !productValue}
            className="neo-button-primary w-full sm:w-auto inline-flex items-center justify-center gap-2"
            data-testid="calculate-btn"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Calcul en cours...</>
            ) : (
              <><Calculator size={18} /> Calculer les frais</>
            )}
          </button>
        )}
      </form>

      {/* Results */}
      {result && (
        <div className="neo-card p-4 sm:p-6 border-[#00E676]" data-testid="customs-result">
          <h2 className="font-extrabold text-base sm:text-lg uppercase tracking-tight mb-4 flex items-center gap-2">
            <Ship size={20} className="text-[#00E676]" />
            {result.corridor_name}
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="neo-card p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Valeur produit</p>
              <p className="text-lg font-extrabold">{result.product_value.toFixed(2)}€</p>
            </div>
            <div className="neo-card p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Livraison</p>
              <p className="text-lg font-extrabold">{result.shipping_cost.toFixed(2)}€</p>
            </div>
            <div className="neo-card p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Total frais</p>
              <p className="text-lg font-extrabold text-[#FF5722]">{result.total_fees.toFixed(2)}€</p>
            </div>
            <div className="neo-card p-3 text-center border-[#00E676]">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Coût total</p>
              <p className="text-lg font-extrabold text-[#00E676]">{result.total_cost.toFixed(2)}€</p>
            </div>
          </div>

          {/* Breakdown */}
          <div className="space-y-2 mb-4">
            <div className="flex justify-between items-center py-2 border-b border-[#0A0A0A]/10 dark:border-white/10">
              <span className="text-sm">Valeur CIF (produit + livraison)</span>
              <span className="font-bold">{result.customs_value.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#0A0A0A]/10 dark:border-white/10">
              <span className="text-sm flex items-center gap-1">
                Droits de douane ({(result.duty_rate * 100).toFixed(1)}%)
                {result.duty_exempt && <span className="text-[10px] bg-[#00E676] text-[#0A0A0A] px-1.5 py-0.5 font-bold">EXEMPTÉ</span>}
              </span>
              <span className="font-bold">{result.duty_amount.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#0A0A0A]/10 dark:border-white/10">
              <span className="text-sm">TVA ({(result.vat_rate * 100).toFixed(0)}%)</span>
              <span className="font-bold">{result.vat_amount.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#0A0A0A]/10 dark:border-white/10">
              <span className="text-sm">Frais de dédouanement</span>
              <span className="font-bold">{result.customs_fee.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center py-3 bg-[#0A0A0A] text-white px-3 -mx-1">
              <span className="font-bold uppercase text-sm">Coût total rendu</span>
              <span className="font-extrabold text-[#00E676] text-lg">{result.total_cost.toFixed(2)}€</span>
            </div>
          </div>

          {/* Notes */}
          <div className="flex items-start gap-2 bg-[#FFCC00]/10 border border-[#FFCC00]/40 p-3">
            <Info size={16} className="text-[#FFCC00] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">{result.notes}</p>
          </div>
        </div>
      )}

      {/* Legal info */}
      <div className="neo-card p-4 sm:p-5">
        <h3 className="font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-[#FFCC00]" />
          Informations importantes — 2026
        </h3>
        <div className="space-y-2 text-xs text-muted-foreground">
          <p><strong>Union Européenne :</strong> Depuis juillet 2021, la TVA s'applique dès le 1er euro sur toutes les importations. Seuil de franchise douanière : 150€. Procédure IOSS (Import One-Stop Shop) disponible.</p>
          <p><strong>Royaume-Uni :</strong> Post-Brexit, les marchandises UK bénéficient du TCA (0% droits) vers l'UE pour les produits d'origine. Seuil TVA UK : £0 depuis 2021.</p>
          <p><strong>États-Unis :</strong> Seuil de minimis de $800. Pas de TVA fédérale, mais taxes d'état variables (0-10%).</p>
          <p><strong>Chine :</strong> Tarifs d'importation majorés depuis les tensions commerciales. TVA chinoise : 13%.</p>
          <p className="text-[10px] italic mt-2">Les taux indiqués sont des moyennes pour les biens de consommation. Les taux réels peuvent varier selon la catégorie HS du produit. Consultez un courtier en douane pour des calculs précis.</p>
        </div>
      </div>
    </div>
  );
}
